import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';

const CustomerAuthContext = createContext();

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const useCustomerAuth = () => {
  const context = useContext(CustomerAuthContext);
  if (!context) {
    throw new Error('useCustomerAuth must be used within CustomerAuthProvider');
  }
  return context;
};

export const CustomerAuthProvider = ({ children }) => {
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/customer/auth/me`, { 
        withCredentials: true 
      });
      setCustomer(data);
    } catch (error) {
      setCustomer(null);
    } finally {
      setLoading(false);
    }
  }, []); // API is a constant, axios/setCustomer/setLoading are stable

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/customer/auth/logout`, {}, { 
        withCredentials: true 
      });
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Logout error:', error);
      }
    } finally {
      setCustomer(null);
    }
  }, []); // API is a constant, axios/setCustomer are stable

  const contextValue = useMemo(() => ({
    customer,
    loading,
    logout,
    isAuthenticated: !!customer,
    checkAuth
  }), [customer, loading, logout, checkAuth]);

  return (
    <CustomerAuthContext.Provider value={contextValue}>
      {children}
    </CustomerAuthContext.Provider>
  );
};
